import { makeStyles, CircularProgress } from '@material-ui/core';
import { ContentCopy } from '@mui/icons-material';
import api from 'api';
import { BoxWrapper, Button, Prompt } from 'components/Base';
import React, { useState, useEffect, useRef } from 'react';
import Store from 'store/store';
import CustomAllocationsGroup from './CustomAllocationsGroup';
import { Formik } from 'formik';
import { formatPercent, Notify, unFormatPercent } from 'utils';
import ErrorIcon from '@material-ui/icons/ErrorOutline';
import FloatingModal from 'components/Base/Modal/FloatingModal/FloatingModal';
import moment from 'moment';
import { isEqual, keyBy, map } from 'lodash';
import { useUnsavedChangesWarning } from '../utils';

const { connect, selectors } = Store;

const useStyles = makeStyles({
  loaderWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    padding: '1rem 0'
  }
});

type CustomTypicalAssetAllocationProps = {
  isFirmAdmin: boolean;
  firmRiskGroup: number;
  fetchedTimeHorizons: any;
  setDirty: Function;
};

const CustomTypicalAssetAllocations = ({
  isFirmAdmin,
  firmRiskGroup,
  fetchedTimeHorizons,
  setDirty
}: CustomTypicalAssetAllocationProps) => {
  const classes = useStyles();
  const [timeHorizons, setTimeHorizons] = useState(fetchedTimeHorizons);
  const [isLoading, setIsLoading] = useState(false);
  const [riskDescriptions, setRiskDescriptions] = useState([]);
  const [assetClasses, setAssetClasses] = useState([]);
  const [isCopyModalVisible, setCopyModalVisible] = useState(false);
  const [clickedRiskLevel, setClickedRiskLevel] = useState(null);
  const [lastEvalueUpdate, setLastEvalueUpdate] = useState('');
  const [isResetModalVisible, setIsResetModalVisible] = useState(false);
  const [isCopyPromptVisible, setIsCopyPromptVisible] = useState(false);
  const [copiedProfile, setCopiedProfile] = useState({});
  const [hasErrors, setHasErrors] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [isLeaveModalVisible, setLeaveModalVisible] = useState(false);
  const [targetLocation, setTargetLocation] = useState(null) as any;

  useUnsavedChangesWarning({
    dirty: isFormDirty,
    confirmLeave,
    targetLocation,
    setLeaveModalVisible,
    setDirty,
    setTargetLocation
  });

  const emptyAllocations = Object.fromEntries(
    Array.from({ length: firmRiskGroup }, (_, i) => [i + 1, {}])
  ) as any;

  const [defaultTypicalAssetAllocation, setDefaultTypicalAssetAllocation] =
    useState(emptyAllocations);
  const [customTypicalAssetAllocation, setCustomTypicalAssetAllocation] =
    useState(emptyAllocations);
  const [clickedIconRef, setClickedIconRef] =
    useState<React.RefObject<HTMLSpanElement> | null>(null);
  const iconContainerRefs = useRef<(HTMLSpanElement | null)[]>([]);

  const validateAllocations = (values: any): any => {
    const errors: any = { allocations: {} as any };

    Object.entries(values.allocations).forEach(
      ([riskLevel, timeHorizonData]) => {
        errors.allocations[riskLevel] = {};

        Object.entries(timeHorizonData as any).forEach(
          ([timeHorizon, data]) => {
            if (data && (data as any).length === 0) {
              return;
            }

            const totalPercent =
              Math.round(
                (data as any).reduce(
                  (sum: number, item: any) =>
                    sum + (Number(unFormatPercent(item.percentage)) || 0),
                  0
                ) * 100
              ) / 100;

            if (totalPercent !== 100) {
              errors.allocations[riskLevel][timeHorizon] = {
                ...errors.allocations[riskLevel][timeHorizon],
                percentage: 'Total percentage must be 100%'
              };
            }

            const missingAssetClass = (data as any).some(
              (item: any) =>
                !item.asset_class &&
                Number(unFormatPercent(item.percentage)) > 0
            );
            if (missingAssetClass) {
              errors.allocations[riskLevel][timeHorizon] = {
                ...errors.allocations[riskLevel][timeHorizon],
                asset_class: 'Each item must have an asset class.'
              };
            }
          }
        );

        if (Object.keys(errors.allocations[riskLevel]).length === 0) {
          delete errors.allocations[riskLevel];
        }
      }
    );

    const formHasErrors = Object.keys(errors.allocations).length > 0;
    setHasErrors(formHasErrors);

    return formHasErrors ? errors : {};
  };

  const handleReplace = (
    profile: any,
    values: any,
    setFieldValue: Function
  ) => {
    setFieldValue(
      `allocations.${clickedRiskLevel}`,
      values[`allocations`][profile.risk_level]
    );
    setCopyModalVisible(false);
  };

  const formatAllocations = (allocations: any) => {
    const formattedAllocations = JSON.parse(JSON.stringify(emptyAllocations));

    allocations.forEach((wrapper: any) => {
      formattedAllocations[wrapper.risk_level][wrapper.time_horizon] =
        wrapper.asset_allocations.map((allocation: any) => {
          return {
            asset_class: allocation?.asset_class?.toString(),
            name: allocation?.name,
            percentage: formatPercent(Number(allocation.percentage) * 100)
          };
        });
    });

    return formattedAllocations;
  };

  const mergeAllocations = (defaults: any, overrides: any) => {
    const overrideMap = keyBy(
      overrides,
      item => `${item.risk_level}-${item.time_horizon}`
    );

    return map(defaults, item => {
      const key = `${item.risk_level}-${item.time_horizon}`;
      return overrideMap[key]
        ? { ...item, asset_allocations: overrideMap[key].asset_allocations } // Override found
        : item; // Keep default
    });
  };
  const prepareAllocations = (allocations: any = []) => {
    return allocations
      .map((item: any) => ({
        ...item,
        percentage: Number(unFormatPercent(item.percentage))
      }))
      .filter((item: any) => item.percentage > 0);
  };

  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      api.getRiskDescriptions(),
      api.getMetaTimeHorizon(),
      api.getAssetClasses(),
      api.getCustomTypicalAssetAllocation(),
      api.getDefaultTypicalAssetAllocation(),
      api.getMetaReturnForecastsConfig()
    ])
      .then(
        ([
          riskDescriptionResponse,
          timeHorizonResponse,
          assetClassesResponse,
          customTypicalAssetAllocationResponse,
          defaultTypicalAssetAllocationResponse,
          forecastReturnsResponse
        ]) => {
          setRiskDescriptions(riskDescriptionResponse.data);
          setTimeHorizons(timeHorizonResponse.data);
          setAssetClasses(assetClassesResponse.data);
          setLastEvalueUpdate(
            forecastReturnsResponse.data.most_recent_evalue_update
          );

          const defaults = formatAllocations(
            defaultTypicalAssetAllocationResponse.data.results
          );

          setDefaultTypicalAssetAllocation(defaults);

          if (customTypicalAssetAllocationResponse.data.results.length > 0) {
            setCustomTypicalAssetAllocation(
              formatAllocations(
                mergeAllocations(
                  defaultTypicalAssetAllocationResponse.data.results,
                  customTypicalAssetAllocationResponse.data.results
                )
              )
            );
          } else {
            setCustomTypicalAssetAllocation(defaults);
          }
        }
      )
      .finally(() => {
        setIsLoading(false);
      });
  }, [firmRiskGroup]);

  const checkIsDefault = (defaultAllocations: any, customAllocations: any) => {
    return isEqual(
      prepareAllocations(customAllocations),
      prepareAllocations(defaultAllocations)
    );
  };

  const save = (values: any, method = 'saved') => {
    if (hasErrors) {
      Notify.error(
        'Failed to save.',
        'Please review your inputs. Some fields are missing or incorrect.'
      );
      return;
    }

    const data = Object.entries(values.allocations)
      .map(([riskLevel, timeHorizons]) => {
        return {
          riskLevel,
          timeHorizons: Object.entries(timeHorizons as any)
            .map(([timeHorizon, items]) => ({
              timeHorizon,
              items: prepareAllocations(items)
            }))
            .filter(
              timeHorizon =>
                !checkIsDefault(
                  defaultTypicalAssetAllocation[riskLevel]?.[
                    timeHorizon.timeHorizon
                  ],
                  timeHorizon.items
                )
            )
        };
      })
      .filter((item: any) => item.timeHorizons.length > 0);

    api
      .updateCustomTypicalAssetAllocation(data)
      .then(() => {
        Notify.success(`Typical asset allocations ${method} successfully.`);
        setDirty(false);
      })
      .catch(() => {
        Notify.error(`Typical asset allocations could not be ${method}.`);
      });
  };

  const reset = (setFieldValue: Function) => {
    setFieldValue('allocations', defaultTypicalAssetAllocation);
    save({ allocations: emptyAllocations }, 'reset');
    setIsResetModalVisible(false);
  };

  return (
    <div style={{ marginTop: '1rem' }}>
      <BoxWrapper
        title="Typical Asset Allocation"
        subTitle="Customise the default typical asset allocations for each risk profile to align with your firm's investment strategy or preferences. These allocations will replace the default EV-provided allocations and will be reflected in the risk analysis page and client reports. Note: Changes made here will apply across your firm."
        dropdown
      >
        {isLoading ? (
          <div className={classes.loaderWrapper}>
            <CircularProgress size="80px" />
          </div>
        ) : (
          <Formik
            enableReinitialize
            initialValues={{
              allocations: customTypicalAssetAllocation
            }}
            validate={validateAllocations}
            validateOnBlur={false}
            validateOnChange
            onSubmit={values => {
              save(values);
            }}
          >
            {({ handleSubmit, values, errors, setFieldValue, dirty }) => {
              setIsFormDirty(dirty);
              return (
                <form onSubmit={handleSubmit}>
                  {riskDescriptions.map((item: any, index: number) => {
                    const scopedErrors = (errors.allocations as any)?.[
                      item.risk_level
                    ];

                    return (
                      <BoxWrapper
                        title={`${item.risk_level}: ${item.risk_type}`}
                        key={index}
                        dropdown
                        item
                        alignHeaderChildrenLeft
                        headerChildren={
                          <span
                            style={{
                              display: 'flex',
                              gap: '1rem',
                              position: 'relative'
                            }}
                            ref={el => (iconContainerRefs.current[index] = el)}
                          >
                            <ContentCopy
                              fontSize="small"
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                const ref = iconContainerRefs.current[index];
                                if (ref) {
                                  setClickedIconRef({ current: ref });
                                }
                                setCopyModalVisible(!isCopyModalVisible);
                                setClickedRiskLevel(item.risk_level);
                              }}
                              style={{
                                transition: 'color 0.2s'
                              }}
                              onMouseEnter={(e: any) =>
                                (e.currentTarget.style.color = 'grey')
                              }
                              onMouseLeave={(e: any) =>
                                (e.currentTarget.style.color = 'black')
                              }
                            />
                            {scopedErrors && <ErrorIcon color="error" />}
                            {isCopyModalVisible &&
                              clickedRiskLevel == item.risk_level && (
                                <FloatingModal
                                  buttonRef={clickedIconRef}
                                  onClose={() => setCopyModalVisible(false)}
                                  adjust={false}
                                >
                                  Copy from risk profile:
                                  <div
                                    style={{
                                      display: 'flex',
                                      flexWrap: 'wrap',
                                      gap: '0.5rem',
                                      marginTop: '0.5rem',
                                      width: '250px'
                                    }}
                                  >
                                    {riskDescriptions
                                      .filter(
                                        (profile: any) =>
                                          profile.risk_level !==
                                          clickedRiskLevel
                                      )
                                      .map((profile: any) => (
                                        <Button
                                          size="small"
                                          variant="contained"
                                          color="secondary"
                                          key={profile.risk_level}
                                          onClick={e => {
                                            e.stopPropagation();
                                            setCopiedProfile(profile);
                                            setIsCopyPromptVisible(true);
                                          }}
                                        >
                                          {profile.risk_level}
                                        </Button>
                                      ))}
                                  </div>
                                </FloatingModal>
                              )}
                          </span>
                        }
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '.5rem',
                            margin: '1rem'
                          }}
                        >
                          {timeHorizons.map(
                            (timeHorizon: any, index: number) => {
                              const defaults =
                                defaultTypicalAssetAllocation[
                                  item.risk_level
                                ]?.[timeHorizon.value];
                              const isDefault = checkIsDefault(
                                defaults,
                                values.allocations[item.risk_level]?.[
                                  timeHorizon.value
                                ]
                              );
                              return (
                                <CustomAllocationsGroup
                                  key={index}
                                  riskLevel={item.risk_level}
                                  timeHorizon={timeHorizon}
                                  allocations={
                                    values.allocations[item.risk_level]?.[
                                      timeHorizon.value
                                    ] || []
                                  }
                                  assetClasses={assetClasses}
                                  setFieldValue={setFieldValue}
                                  values={{
                                    timeHorizons,
                                    form: values.allocations[item.risk_level]
                                  }}
                                  errors={scopedErrors?.[timeHorizon.value]}
                                  isDefault={isDefault}
                                  defaults={defaults}
                                />
                              );
                            }
                          )}
                        </div>
                      </BoxWrapper>
                    );
                  })}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '1rem',
                      position: 'sticky',
                      bottom: 0,
                      zIndex: 9,
                      borderRadius: '25px',
                      margin: '0 auto',
                      background: '#ffffff',
                      paddingBottom: '1rem'
                    }}
                  >
                    <div>
                      <Button
                        variant="contained"
                        color="primary"
                        style={{ marginRight: '1rem' }}
                        onClick={() => setIsResetModalVisible(true)}
                      >
                        Reset
                      </Button>
                      Reset to default settings (provided by EV) - last updated{' '}
                      {moment(lastEvalueUpdate).format('dddd, MMMM Do YYYY')}
                    </div>
                    <Button
                      type="submit"
                      variant="contained"
                      color="secondary"
                      size="medium"
                      disabled={!isFirmAdmin}
                    >
                      Save
                    </Button>
                  </div>
                  <Prompt
                    isOpen={isResetModalVisible}
                    title={`Are you sure you want to reset all typical asset allocations to their default settings?`}
                    confirmAction={() => reset(setFieldValue)}
                    cancelAction={() => {
                      setIsResetModalVisible(false);
                    }}
                    isProcessing={false}
                  />
                  <Prompt
                    isOpen={isCopyPromptVisible}
                    title={`Are you sure you want to copy these asset allocations? This will replace all current allocations in this risk level.`}
                    confirmAction={() => {
                      handleReplace(copiedProfile, values, setFieldValue);
                      Notify.success(`Asset allocations copied successfully.`);
                      setIsCopyPromptVisible(false);
                    }}
                    cancelAction={() => {
                      setIsCopyPromptVisible(false);
                    }}
                    isProcessing={false}
                  />
                </form>
              );
            }}
          </Formik>
        )}
      </BoxWrapper>
      <Prompt
        isOpen={isLeaveModalVisible}
        title={`You have unsaved changes, are you sure you want to leave?`}
        confirmAction={() => setConfirmLeave(true)}
        cancelAction={() => {
          setTargetLocation(null);
          setLeaveModalVisible(false);
        }}
        isProcessing={false}
      />
    </div>
  );
};

export default connect(
  (state: any) => ({
    firmRiskGroup: selectors.firmSelectors.riskGroup(state),
    fetchedTimeHorizons: selectors.metaSelectors.timeHorizons(state)
  }),
  null
)(CustomTypicalAssetAllocations);
